import React, { useState } from "react";
import { Mail, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import axios from "axios";

const TYPES = [
  { value: "general", label: "General Inquiry" },
  { value: "bug", label: "Bug Report" },
  { value: "affiliate", label: "Affiliate Request" },
];

export default function ContactWidget() {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ type: "general", message: "", email: "", phone: "", company: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim() || !form.email.trim()) {
      toast.error("Please add your email and a message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ type: "general", message: "", email: "", phone: "", company: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="contact-open-button"
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-4 py-3 text-sm font-bold text-black shadow-lg shadow-[var(--accent)]/20 transition-transform hover:scale-105 active:scale-95"
        aria-label="Contact us"
      >
        <Mail size={16} strokeWidth={2.5} />
        <span className="hidden sm:inline">Contact</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !sending && setOpen(false)} data-testid="contact-modal">
          <div className="w-full max-w-md rounded-xl border border-[var(--border)] bg-[#0D0D0D] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h2 className="font-display text-2xl font-bold">Get in touch</h2>
              <button type="button" onClick={() => !sending && setOpen(false)} data-testid="contact-close-button"
                className="text-[var(--text-3)] hover:text-white transition-colors"><X size={20} /></button>
            </div>
            <p className="text-sm text-[var(--text-3)] mb-5">Questions, bugs, or partnership ideas — we read every message.</p>

            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-3 gap-2" data-testid="contact-type-group">
                {TYPES.map((t) => (
                  <button key={t.value} type="button" onClick={() => setForm((f) => ({ ...f, type: t.value }))}
                    data-testid={`contact-type-${t.value}`}
                    className={`rounded-md border px-2 py-2 text-[11px] font-semibold leading-tight transition-colors ${
                      form.type === t.value
                        ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]"
                        : "border-[var(--border)] text-[var(--text-2)] hover:border-[var(--text-3)]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              <textarea required value={form.message} onChange={set("message")} rows={4} maxLength={5000}
                placeholder="How can we help?" data-testid="contact-message-input"
                className="w-full rounded-md border border-[var(--border)] bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] transition-colors" />

              <input required type="email" value={form.email} onChange={set("email")} placeholder="Your email"
                data-testid="contact-email-input"
                className="w-full rounded-md border border-[var(--border)] bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] transition-colors" />

              <input type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)"
                data-testid="contact-phone-input"
                className="w-full rounded-md border border-[var(--border)] bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] transition-colors" />

              {/* Honeypot — hidden from real users */}
              <input type="text" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off"
                aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />

              <button type="submit" disabled={sending} data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2.5 text-sm font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-60">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sending…" : "Send message"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
